import Image from 'next/image'
import { Camera } from 'src/assets/images'
import { CustomCol } from 'src/components'
import { useTranslation } from 'next-i18next'
import { useWindowSize } from 'src/app/hooks'
import React, { useState, useEffect } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import styles from './CustomImageInput.module.scss'
import { getImageDimensionsSizeOf, getFileSizeInMB } from 'src/core/helpers'
import { Button, Row, Upload, Image as AntdImage, Form, UploadProps, Popconfirm } from 'antd'
interface CustomImageInputProps extends UploadProps {
  name: string,
  required?: boolean
  value?: string
  // eslint-disable-next-line no-unused-vars
  onChangeFileUrl?: (value: string) => void
  imagePreviewWidth?: number
  mobilePreviewWidth?: number
  addResolutionValdiation?: boolean
}

const CustomImageInput: React.FC<CustomImageInputProps> = (props) => {
  const {
    name,
    required = false,
    onChangeFileUrl = null,
    value = null,
    imagePreviewWidth = 185,
    mobilePreviewWidth = 240,
    addResolutionValdiation = true,
    ...restProps
  } = props
  const { t } = useTranslation()
  const [imageSource, setImageSource] = useState(value)
  const [validationMessage, setValidationMessage] = useState('')

  const { width } = useWindowSize()
  const isMobile = width < 800

  const handleFileChange = ({ file }: { file: any }) => { manageFileChange(file) }

  const handleResetImage = () => {
    setImageSource(null)
    if (onChangeFileUrl) {
      onChangeFileUrl(null)
    }
  }

  const getValue = ({ file }) => {
    manageFileChange(file)
    return file
  }

  const manageFileChange = async (file: File) => {
    const dimensions = await getImageDimensionsSizeOf(file)
    const fileSize = getFileSizeInMB(file)
    setValidationMessage('')
    if (addResolutionValdiation && (dimensions.width < 1280 || dimensions.height < 720)) {
      setValidationMessage('general.MinimumResolution')
      return
    }
    if (fileSize > 2) {
      setValidationMessage('general.MaximumSize')
      return
    }
    if (file?.type?.indexOf('image/') >= 0) {
      setImageSource(URL.createObjectURL(file))
    }

    if (onChangeFileUrl) {
      onChangeFileUrl(URL.createObjectURL(file))
    }
  }

  useEffect(() => {
    if (!value) {
      setImageSource(null)
    }
  }, [value])

  return (
    <Row className={styles.content}>
      <CustomCol lg={11}>
        {imageSource ? (
          <div className={styles.imageContent}>
            <AntdImage
              width={isMobile ? mobilePreviewWidth : imagePreviewWidth}
              height={120}
              alt='project-image'
              src={imageSource}
              preview={{ src: imageSource }}
              className={styles.image}
            />
          </div>
        ) : (
          <div className={styles.emptyImage}>
            <Image src={Camera} width={56} height={56} alt='empty-image' />
          </div>
        )}

      </CustomCol>
      <CustomCol lg={13}>
        <Form.Item
          name={name}
          getValueFromEvent={getValue}
          className={styles.uploadInput}
          rules={[
            {
              message: validationMessage ? t(`${validationMessage}`) : t('general.RequiredValidation'),
              validator: () => {
                if (validationMessage) {
                  return Promise.reject(t(`${validationMessage}`))
                }
                if (!required) {
                  return Promise.resolve()
                } else if (required && !imageSource) {
                  return Promise.reject(t('general.RequiredValidation'))
                } else { return Promise.resolve() }
              }
            }
          ]}
        >
          <Upload
            beforeUpload={() => false}
            accept='image/*'
            fileList={[]}
            onChange={handleFileChange}
            showUploadList={false}
            {...restProps}
          >

            <Button
              htmlType='button'
              icon={<UploadOutlined />}
              className={styles.uploadButton}
            >
              {t('Projects.UploadImage')}
            </Button>
          </Upload>
        </Form.Item>

        {imageSource && (
          <Popconfirm
            title={t('general.SureDelete')}
            onConfirm={handleResetImage}
            okText={t('general.Yes')}
            cancelText={t('general.Cancel')}
          >
            <Button
              type='text'
              htmlType='button'
              size={'large'}
              className={styles.resetButton}
            >
              {t('Projects.RemoveImage')}
            </Button>
          </Popconfirm>

        )}
      </CustomCol>
    </Row>
  )
}

export default CustomImageInput
